import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ArticleNumbersFieldComponent, ReportService } from '../common';
import { CompanySite, Page, ReportArchive } from '../model';
import { formatJsonDate } from '../util';
import { ReportOverviewCardComponent } from './report-overview-card.component';
// PDFPreviewDialogComponent is opened directly via MatDialog rather than through the shared
// `Dialog` service: `Dialog` imports this component to wire up `tracesReportSelector`, so
// importing `Dialog` back here would create a circular import.
import { PDFPreviewDialogComponent } from '../company/evaluation/pdf-preview-dialog.component';

interface DDSRecord {
  ddr: string;
  ddv: string;
  checkresult?: string;
  dateReceived: Date;
  tags?: string[];
  comment?: string;
  checkresultParsed?: any;
}

export interface ReportSelectorDialogData {
  product: FormGroup<{
    name: FormControl<string>;
    hsCode: FormControl<string>;
    notes: FormControl<string>;
    weight: FormControl<number>;
    articleNumbers: FormControl<string[]>;
    dds: FormControl<DDSRecord[]>;
    validityDate: FormControl<Date | null>;
    selectedReports: FormControl<string[]>;
  }>;
  site: CompanySite;
  reportCache: Map<string, ReportArchive>;
}

@Component({
  selector: 'app-traces-dds-report-selector-dialog',
  templateUrl: './traces-dds-report-selector-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatIconModule,
    TranslatePipe,
    ArticleNumbersFieldComponent,
    ReportOverviewCardComponent,
  ],
  styleUrl: './traces-dds-report-selector-dialog.component.scss',
})
export class TracesDdsReportSelectorDialogComponent implements OnInit, OnDestroy {
  private readonly service = inject(ReportService);
  private readonly matDialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<TracesDdsReportSelectorDialogComponent>);
  private readonly data = inject<ReportSelectorDialogData>(MAT_DIALOG_DATA);
  private readonly destroy$ = new Subject<void>();

  product = this.data.product;
  site = this.data.site;
  reportCache = this.data.reportCache;
  matchingReports?: { missingArticleNumbers: string[] } & Page<ReportArchive>;
  hideUnmatchedWarning = false;

  /**
   * The dialog's own article-number field, seeded from the main form's current value. It drives
   * search only — it must never write back into `product.articleNumbers`, which the main form
   * keeps editing independently (R4).
   */
  articleNumbers = new FormControl<string[]>([...(this.product.value.articleNumbers || [])], {
    nonNullable: true,
  });

  currentPage = 0;
  isLoadingMore = false;
  hasMorePages = true;

  async ngOnInit() {
    this.articleNumbers.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.searchReports());

    this.product
      .get('validityDate')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.searchReports());

    await this.searchReports();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async searchReports(): Promise<void> {
    try {
      this.currentPage = 0;
      this.hasMorePages = true;
      this.hideUnmatchedWarning = false;
      this.matchingReports = await this.service.searchReportsByArticles(
        { page: this.currentPage },
        this.site.id,
        this.articleNumbers.value,
        formatJsonDate(this.product.value.validityDate)
      );
      this.matchingReports.content.forEach((report) => this.reportCache.set(report.id, report));
      this.hasMorePages = this.currentPage < this.matchingReports.totalPages - 1;
    } catch (error) {
      console.error('Error searching reports:', error);
      this.matchingReports = undefined;
    }
  }

  async loadNextPage() {
    // Prevent loading if already loading or no more pages
    if (this.isLoadingMore || !this.hasMorePages || !this.matchingReports) {
      return;
    }

    try {
      this.isLoadingMore = true;
      this.currentPage++;

      const result = await this.service.searchReportsByArticles(
        { page: this.currentPage },
        this.site.id,
        this.articleNumbers.value,
        formatJsonDate(this.product.value.validityDate)
      );

      this.matchingReports.content.push(...result.content);
      result.content.forEach((report) => this.reportCache.set(report.id, report));
      this.matchingReports.pageNumber = result.pageNumber;
      this.hasMorePages = this.currentPage < result.totalPages - 1;
    } catch (error) {
      console.error('Error loading next page:', error);
      this.currentPage--;
    } finally {
      this.isLoadingMore = false;
    }
  }

  async onScroll(event: Event) {
    const { clientHeight, scrollHeight, scrollTop } = event.target as HTMLElement;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      await this.loadNextPage();
    }
  }

  toggleReportSelection(reportId: string): void {
    const reports = this.product.value.selectedReports || [];
    const selectedReports =
      reports.indexOf(reportId) >= 0 ? reports.filter((id) => id !== reportId) : [...reports, reportId];
    this.product.patchValue({ selectedReports });
    this.product.get('selectedReports')?.markAsDirty();
  }

  removeReport(reportId: string): void {
    const selectedReports = this.product.value.selectedReports || [];
    const newSelection = selectedReports.filter((id) => id !== reportId);
    this.product.patchValue({ selectedReports: newSelection });
    this.product.get('selectedReports')?.markAsDirty();
  }

  isReportSelected(reportId: string): boolean {
    return (this.product.value.selectedReports || []).includes(reportId);
  }

  // A report can be added only when it carries geodata (degrees). Guards the backend
  // `tiers`->`degrees` DTO rename — see memory project_dto_field_rename_frontend_drift.
  canAddReport(report: ReportArchive): boolean {
    return !this.isReportSelected(report.id) && (!!report.degrees?.length || report.recycledVerdict === 'FULL');
  }

  async previewReport(report: ReportArchive): Promise<void> {
    const { body } = await this.service.downloadArchivePdf(report);
    if (body) {
      this.matDialog.open(PDFPreviewDialogComponent, {
        autoFocus: false,
        width: '100vw',
        maxWidth: 'calc(100vw - 30px)',
        height: 'calc(100vh - 50px)',
        data: { pdf: body, title: `Report: ${report.name} / ${report.norm.name}` },
      });
    }
  }

  hideWarning(): void {
    this.hideUnmatchedWarning = true;
  }

  close(): void {
    this.dialogRef.close();
  }
}
